<template>
    <div class="flex flex-col gap-5">
        <!-- Seletor de período + dossiê: renderizados na toolbar única do UserAudit.
             Sem o alvo (outra composição de rota), rendem inline no topo da página. -->
        <Teleport
            v-if="mounted"
            to="#audit-toolbar"
            :disabled="!toolbarTargetExists"
        >
            <div class="flex min-w-0 flex-wrap items-center gap-3">
                <div
                    role="group"
                    aria-label="Selecionar período de análise"
                    v-tooltip="'Janela de análise: ' + periodRangeLabel"
                    class="inline-flex items-center gap-0.5 rounded-full bg-gray-100 p-0.5 dark:bg-gray-800"
                >
                    <button
                        v-for="option in periodOptions"
                        :key="option"
                        type="button"
                        :aria-pressed="period === option"
                        @click="period = option"
                        :class="[
                            'rounded-full px-2.5 py-1 text-xs font-medium transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900',
                            period === option
                                ? 'bg-indigo-600 text-white shadow-sm'
                                : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white',
                        ]"
                    >
                        {{ option === 0 ? "Total" : option + "d" }}
                    </button>
                </div>
                <span
                    class="hidden text-xs text-gray-400 dark:text-gray-500 lg:inline"
                >
                    {{ periodRangeLabel }}
                </span>

                <button
                    @click="generateDossier"
                    :disabled="generatingDossier"
                    v-tooltip="'Gerar dossiê do usuário'"
                    class="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white transition duration-150 ease-in-out hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                    <svg
                        v-if="!generatingDossier"
                        class="h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        ></path>
                    </svg>
                    <svg
                        v-else
                        class="h-4 w-4 animate-spin text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                    >
                        <circle
                            class="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            stroke-width="4"
                        ></circle>
                        <path
                            class="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                    </svg>
                    {{ generatingDossier ? "Gerando..." : "Dossiê" }}
                </button>
            </div>
        </Teleport>

        <!-- Farol: a leitura de 10 segundos e o seletor de contexto. Cada
             tile responde uma pergunta e abre a aba que a sustenta. -->
        <section class="flex flex-col gap-1.5">
            <seller-farol
                :signals="farolSignals"
                :loading="portraitLoading"
                :active="activeSection"
                @go="activeSection = $event"
            />
            <p
                v-if="portrait"
                class="self-end font-mono text-[10px] tabular-nums text-gray-400 dark:text-gray-500"
            >
                {{ portraitMetaLabel }}
            </p>
        </section>

        <!-- Painéis das abas: v-show (não v-if) para os cards continuarem
             montados e alimentando os resumos que o farol usa. -->

        <!-- CAIXA: posso liberar dinheiro deste seller? -->
        <section
            v-show="activeSection === 'caixa'"
            role="tabpanel"
            aria-label="Caixa: posso liberar dinheiro?"
            class="flex flex-col gap-3"
        >
            <seller-portrait-grid
                :portrait="portrait"
                :loading="portraitLoading"
                :error="portraitError"
                :keys="['coverage', 'exposure', 'reserve', 'cashouts']"
                @open-detail="openSnapshotDetail"
            />

            <div
                class="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700"
            >
                <collapsible-info-card
                    title="Reserva &amp; Runoff"
                    icon="ScaleIcon"
                    :collapsed="runoffCollapsed"
                    :priority="runoffPriority"
                    :summary="runoffSummary"
                    :badge="runoffBadge"
                    :loading="runoffSummary === ''"
                >
                    <reserve-runoff-card
                        :user="user"
                        @data-loaded="updateRunoffSummary"
                    />
                </collapsible-info-card>
            </div>
        </section>

        <!-- OPERAÇÃO: o negócio está vivo? -->
        <section
            v-show="activeSection === 'operacao'"
            role="tabpanel"
            aria-label="Operação: o negócio está vivo?"
            class="flex flex-col gap-3"
        >
            <seller-portrait-grid
                :portrait="portrait"
                :loading="portraitLoading"
                :error="portraitError"
                :keys="['tpv', 'sales_count', 'days_with_sale', 'card_share']"
                @open-detail="openSnapshotDetail"
            />

            <div class="mt-2 flex flex-col gap-4">
                <div class="flex items-center gap-2">
                    <h3
                        id="commissions-heading"
                        class="text-sm font-semibold text-gray-900 dark:text-white"
                    >
                        Comissões
                    </h3>
                    <span
                        class="inline-flex items-center rounded-full bg-indigo-50 px-2.5 py-0.5 text-xs font-medium text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300"
                    >
                        {{ periodLabel }}
                    </span>
                </div>

                <commission-cards
                    :user="user"
                    :period="period"
                ></commission-cards>

                <commissions-chart
                    :user="user"
                    :filters="chartFilters"
                    :period="period"
                />
            </div>

            <div
                class="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700"
            >
                <collapsible-info-card
                    title="Métodos de Pagamento"
                    icon="BanknotesIcon"
                    :collapsed="true"
                    :summary="paymentMethodSummary"
                    :priority="paymentMethodPriority"
                    :badge="paymentMethodBadge"
                    :period-label="periodLabel"
                    :loading="paymentMethodSummary === ''"
                >
                    <user-payment-method-card
                        :user="user"
                        :period="period"
                        @data-loaded="updatePaymentMethodSummary"
                    />
                </collapsible-info-card>

                <collapsible-info-card
                    title="Tipos de Comissão"
                    icon="ChartBarIcon"
                    :collapsed="true"
                    :summary="commissionTypesSummary"
                    :priority="commissionTypesPriority"
                    :badge="commissionTypesBadge"
                    :loading="commissionTypesSummary === ''"
                >
                    <user-commission-types-card
                        :user="user"
                        @data-loaded="updateCommissionTypesSummary"
                    />
                </collapsible-info-card>
            </div>
        </section>

        <!-- CONDUTA: tem sinal de fraude ou abuso? -->
        <section
            v-show="activeSection === 'conduta'"
            role="tabpanel"
            aria-label="Conduta: tem sinal de fraude?"
            class="flex flex-col gap-3"
        >
            <seller-portrait-grid
                :portrait="portrait"
                :loading="portraitLoading"
                :error="portraitError"
                :keys="['disputes', 'box_disputes', 'refunds']"
                @open-detail="openSnapshotDetail"
            />

            <div
                class="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700"
            >
                <collapsible-info-card
                    title="Score"
                    icon="StarIcon"
                    :collapsed="false"
                    :priority="scorePriority"
                    :summary="scoreSummary"
                    :badge="scoreBadge"
                    :loading="scoreSummary === ''"
                >
                    <score :user="user" @data-loaded="updateScoreSummary" />
                </collapsible-info-card>

                <collapsible-info-card
                    title="Análise do Sistema"
                    icon="ExclamationCircleIcon"
                    :collapsed="!userAnalysis"
                    :priority="systemAnalysisPriority"
                    :summary="systemAnalysisSummary"
                    :badge="systemAnalysisBadge"
                    :loading="!userAnalysis"
                >
                    <user-system-analysis
                        :user="user"
                        @data-loaded="updateSystemAnalysisSummary"
                    />
                </collapsible-info-card>

                <collapsible-info-card
                    title="BINs de Cartão Mais Usados"
                    icon="CreditCardIcon"
                    :collapsed="true"
                    :summary="binSummary"
                    :priority="binPriority"
                    :badge="binBadge"
                    :period-label="periodLabel"
                    :loading="binSummary === ''"
                >
                    <user-bin-usage-card
                        :user="user"
                        :period="period"
                        @data-loaded="updateBinSummary"
                    />
                </collapsible-info-card>
            </div>
        </section>

        <!-- Detalhamento completo (fichas, escalas, evidências): a camada de
             auditoria dos números. Clique em um card do retrato abre aqui. -->
        <section ref="snapshotDetail" class="flex scroll-mt-4 flex-col gap-3">
            <button
                type="button"
                class="flex items-center gap-2 self-start text-[11px] font-medium uppercase tracking-[0.14em] text-gray-500 transition-colors hover:text-indigo-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:text-gray-400 dark:hover:text-indigo-400"
                :aria-expanded="showSnapshotDetail"
                @click="showSnapshotDetail = !showSnapshotDetail"
            >
                <svg
                    class="h-3.5 w-3.5 transition-transform"
                    :class="{ 'rotate-90': showSnapshotDetail }"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M9 5l7 7-7 7"
                    ></path>
                </svg>
                Detalhamento do retrato (fichas, réguas e evidências)
            </button>
            <seller-snapshot-panel v-if="showSnapshotDetail" :user="user" />
        </section>
    </div>
</template>

<script>
import CommissionCards from "../../../components/user/CommissionCards.vue";
import Score from "../../../components/user/Score.vue";
import SellerFarol from "../../../components/user/metrics/SellerFarol.vue";
import SellerSnapshotPanel from "../../../components/user/metrics/SellerSnapshotPanel.vue";
import SellerPortraitGrid from "../../../components/user/metrics/SellerPortraitGrid.vue";
import HelperMixin from "../../../mixins/HelperMixin.vue";
import CommissionsChart from "../../users/commissions/CommissionsChart.vue";
import UserBinUsageCard from "../../../components/user/UserBinUsageCard.vue";
import {
    ExclamationCircleIcon,
    CreditCardIcon,
    BanknotesIcon,
    ChartBarIcon,
    ScaleIcon,
    StarIcon,
} from "@heroicons/vue/24/outline";
import ReserveRunoffCard from "../../../components/user/metrics/ReserveRunoffCard.vue";
import UserSystemAnalysis from "../../../components/user/UserSystemAnalysis.vue";
import UserPaymentMethodCard from "../../../components/user/UserPaymentMethodCard.vue";
import UserCommissionTypesCard from "../../../components/user/UserCommissionTypesCard.vue";
import CollapsibleInfoCard from "../../../components/user/CollapsibleInfoCard.vue";

const STATUS_RANK = { critical: 2, attention: 1 };

export default {
    components: {
        CommissionsChart,
        SellerFarol,
        SellerSnapshotPanel,
        SellerPortraitGrid,
        Score,
        CommissionCards,
        UserBinUsageCard,
        ExclamationCircleIcon,
        CreditCardIcon,
        BanknotesIcon,
        ChartBarIcon,
        ScaleIcon,
        StarIcon,
        ReserveRunoffCard,
        UserSystemAnalysis,
        UserPaymentMethodCard,
        UserCommissionTypesCard,
        CollapsibleInfoCard,
    },
    props: ["user"],
    data() {
        return {
            filters: {
                text: "",
                status: [],
                payment_method: [],
            },
            // Janela de análise aplicada a todas as métricas temporais da tela
            period: 30,
            // 0 = Total (sem janela, todo o histórico)
            periodOptions: [7, 15, 30, 90, 0],
            // Retrato: buscado uma vez aqui e distribuído para o farol e as
            // três seções (Caixa / Operação / Conduta).
            portrait: null,
            portraitLoading: true,
            portraitError: false,
            portraitSeq: 0,
            // Dados para os summaries dos cards
            userAnalysis: null,
            scoreSummary: "",
            scorePriority: "normal",
            scoreBadge: null,
            binSummary: "",
            binPriority: "normal",
            binBadge: null,
            paymentMethodSummary: "",
            paymentMethodPriority: "normal",
            paymentMethodBadge: null,
            systemAnalysisSummary: "",
            systemAnalysisPriority: "normal",
            systemAnalysisBadge: null,
            commissionTypesSummary: "",
            commissionTypesPriority: "normal",
            commissionTypesBadge: null,
            runoffSummary: "",
            runoffPriority: "normal",
            runoffBadge: null,
            // Abre sozinho quando o runoff carrega descoberto/apertado: a
            // decisão de segurar parcela não pode ficar atrás de um clique.
            runoffCollapsed: true,
            // Estado do botão de gerar dossiê
            generatingDossier: false,
            // Aba ativa do retrato (caixa | operacao | conduta)
            activeSection: "caixa",
            // Teleport da toolbar: só depois do mount, com fallback inline
            mounted: false,
            toolbarTargetExists: false,
            // Painel detalhado do retrato (fichas e evidências), colapsado
            showSnapshotDetail: false,
        };
    },

    mixins: [HelperMixin],

    computed: {
        chartFilters() {
            return {
                ...this.filters,
                interval: this.period,
            };
        },

        periodLabel() {
            return this.period === 0 ? "Total" : `Últimos ${this.period} dias`;
        },

        periodRangeLabel() {
            if (this.period === 0) {
                return "todo o histórico";
            }

            const start = new Date();
            start.setDate(start.getDate() - this.period);

            const format = (date) =>
                date.toLocaleDateString("pt-BR", {
                    day: "2-digit",
                    month: "2-digit",
                });

            return `${format(start)} – ${format(new Date())}`;
        },

        portraitMetaLabel() {
            const parts = [
                this.period === 0
                    ? "todo o histórico"
                    : `últimos ${this.period} dias`,
            ];

            if (this.portrait?.series_capped) {
                parts.push(`série dos últimos ${this.portrait.series_days}d`);
            }

            if (this.portrait?.cache_minutes) {
                parts.push(`cache ${this.portrait.cache_minutes}min`);
            }

            return parts.join(" · ");
        },

        /**
         * Os três tiles do farol, derivados dos status que já estão na tela
         * (cards do retrato + veredito do runoff). Sem request extra e sem
         * divergir do que as seções mostram.
         */
        farolSignals() {
            const caixa = this.worstCard(["coverage", "reserve", "cashouts"]);
            const operacao = this.worstCard(["days_with_sale"]);
            const conduta = this.worstCard([
                "disputes",
                "box_disputes",
                "refunds",
            ]);

            const signals = [
                {
                    key: "caixa",
                    domain: "Caixa",
                    question: "Posso liberar dinheiro?",
                    ...this.signalOf(
                        caixa,
                        "cobertura e saques dentro do limiar",
                    ),
                },
                {
                    key: "operacao",
                    domain: "Operação",
                    question: "O negócio está vivo?",
                    ...this.signalOf(operacao, "vendendo em ritmo normal"),
                },
                {
                    key: "conduta",
                    domain: "Conduta",
                    question: "Tem sinal de fraude?",
                    ...this.signalOf(
                        conduta,
                        "disputas e reembolsos dentro do limiar",
                    ),
                },
            ];

            // O runoff pode ser pior que os cards de caixa (parcela descoberta)
            const runoffRank =
                { danger: 2, warning: 1 }[this.runoffPriority] ?? 0;

            if (runoffRank > (STATUS_RANK[caixa?.status] ?? 0)) {
                signals[0].status = runoffRank === 2 ? "critical" : "attention";
                signals[0].reason = this.runoffSummary || signals[0].reason;
            }

            return signals;
        },
    },

    watch: {
        period() {
            // Evita misturar valores de janelas diferentes: some o resumo até o novo chegar
            this.binSummary = "";
            this.paymentMethodSummary = "";
            this.fetchPortrait();
        },

        user() {
            this.fetchPortrait();
        },
    },

    mounted() {
        this.toolbarTargetExists = Boolean(
            document.getElementById("audit-toolbar"),
        );
        this.mounted = true;
        this.fetchPortrait();
    },

    methods: {
        fetchPortrait() {
            const seq = ++this.portraitSeq;

            this.portraitLoading = true;
            this.portraitError = false;

            axios
                .get(`/api/user/${this.userId()}/metrics/portrait`, {
                    params: { period: this.period },
                })
                .then(({ data }) => {
                    // O operador pode trocar de período antes da resposta
                    // anterior chegar: só publica a última pedida.
                    if (seq === this.portraitSeq) {
                        this.portrait = data;
                    }
                })
                .catch(() => {
                    if (seq === this.portraitSeq) {
                        this.portraitError = true;
                    }
                })
                .finally(() => {
                    if (seq === this.portraitSeq) {
                        this.portraitLoading = false;
                    }
                });
        },

        userId() {
            return this.$route?.params?.user_id ?? this.user.id;
        },

        worstCard(keys) {
            const byKey = new Map(
                (this.portrait?.cards || []).map((card) => [card.key, card]),
            );

            let worst = null;

            for (const key of keys) {
                const card = byKey.get(key);

                if (
                    card &&
                    (!worst ||
                        (STATUS_RANK[card.status] ?? 0) >
                            (STATUS_RANK[worst.status] ?? 0))
                ) {
                    worst = card;
                }
            }

            return worst;
        },

        signalOf(card, okReason) {
            if (card && (STATUS_RANK[card.status] ?? 0) > 0) {
                return {
                    status: card.status,
                    reason: `${card.label}: ${card.value}`,
                };
            }

            return { status: "ok", reason: okReason };
        },

        openSnapshotDetail() {
            this.showSnapshotDetail = true;

            this.$nextTick(() => {
                this.$refs.snapshotDetail?.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                });
            });
        },

        updateScoreSummary(scoreData) {
            if (!scoreData) {
                this.scoreSummary = "Score não disponível";
                this.scorePriority = "normal";
                this.scoreBadge = null;
                return;
            }

            const { amount, level, bypass, historical } = scoreData;

            // Determina a prioridade baseada no score (escala 15-100, bloqueio em 50)
            if (amount < 50) {
                this.scorePriority = "danger";
            } else if (amount < 75) {
                this.scorePriority = "warning";
            } else {
                this.scorePriority = "normal";
            }

            // Badge com informações especiais
            let badgeText = level.charAt(0).toUpperCase() + level.slice(1);
            if (bypass) {
                badgeText = "Bypass ativo";
                this.scorePriority = "warning";
            } else if (historical) {
                badgeText = "Histórico";
            }

            this.scoreBadge = badgeText;
            this.scoreSummary = `Score ${amount} (${level})${bypass ? " - Bypass ativo" : ""}${historical ? " - Visualizando histórico" : ""}`;
        },

        updateBinSummary(data) {
            if (!data || !data.top_bins || data.top_bins.length === 0) {
                this.binSummary = "Nenhum BIN encontrado";
                this.binPriority = "normal";
                this.binBadge = null;
                return;
            }

            const topBins = data.top_bins;
            const summary = data.summary;

            // Determina prioridade baseada no resumo geral se disponível
            if (summary && summary.high_fraud_bins > 0) {
                this.binPriority = "danger";
                this.binBadge = `${summary.high_fraud_bins}/${summary.total_unique_bins} alto risco`;
                this.binSummary = `${summary.total_unique_bins} BINs, ${summary.high_fraud_bins} alto risco`;
            } else if (summary && summary.medium_fraud_bins > 0) {
                this.binPriority = "warning";
                this.binBadge = `${summary.medium_fraud_bins}/${summary.total_unique_bins} médio risco`;
                this.binSummary = `${summary.total_unique_bins} BINs, ${summary.medium_fraud_bins} médio risco`;
            } else {
                // Fallback para análise dos top bins se não houver resumo
                const highFraudBins = topBins.filter(
                    (bin) => bin.fraud_level === "high",
                ).length;
                const mediumFraudBins = topBins.filter(
                    (bin) => bin.fraud_level === "medium",
                ).length;
                const totalBins = summary?.total_unique_bins || topBins.length;

                if (highFraudBins > 0) {
                    this.binPriority = "danger";
                    this.binBadge = `${highFraudBins}/${totalBins} alto risco`;
                    this.binSummary = `${totalBins} BINs, ${highFraudBins} alto risco`;
                } else if (mediumFraudBins > 0) {
                    this.binPriority = "warning";
                    this.binBadge = `${mediumFraudBins}/${totalBins} médio risco`;
                    this.binSummary = `${totalBins} BINs, ${mediumFraudBins} médio risco`;
                } else {
                    this.binPriority = "normal";
                    this.binBadge = `${totalBins} BINs`;
                    this.binSummary = `${totalBins} BINs únicos`;
                }
            }
        },

        updatePaymentMethodSummary(data) {
            if (!data || data.length === 0) {
                this.paymentMethodSummary = "Nenhum método encontrado";
                this.paymentMethodPriority = "normal";
                this.paymentMethodBadge = null;
                return;
            }

            const highConcentration = data.filter(
                (method) => method.percentage > 70,
            ).length;
            const mediumConcentration = data.filter(
                (method) => method.percentage > 50,
            ).length;

            if (highConcentration > 0) {
                this.paymentMethodPriority = "warning";
                this.paymentMethodBadge = "Alta concentração";
                this.paymentMethodSummary = `${data.length} métodos, concentração alta`;
            } else if (mediumConcentration > 0) {
                this.paymentMethodPriority = "warning";
                this.paymentMethodBadge = "Concentração média";
                this.paymentMethodSummary = `${data.length} métodos, concentração média`;
            } else {
                this.paymentMethodPriority = "normal";
                this.paymentMethodBadge = `${data.length} métodos`;
                this.paymentMethodSummary = `${data.length} métodos distribuídos`;
            }
        },

        updateSystemAnalysisSummary(analysis) {
            this.userAnalysis = analysis;

            if (!analysis) {
                this.systemAnalysisSummary = "Análise não disponível";
                this.systemAnalysisPriority = "normal";
                this.systemAnalysisBadge = null;
                return;
            }

            // Determina a prioridade baseada na confiança e análise
            if (analysis.analysis === "Reprovar" || analysis.confidence < 30) {
                this.systemAnalysisPriority = "danger";
            } else if (
                analysis.analysis === "Atenção" ||
                (analysis.confidence >= 30 && analysis.confidence < 70)
            ) {
                this.systemAnalysisPriority = "warning";
            } else {
                this.systemAnalysisPriority = "normal";
            }

            this.systemAnalysisBadge = `${analysis.confidence}%`;
            this.systemAnalysisSummary = `${analysis.analysis} (${analysis.confidence}% confiança)`;
        },

        updateCommissionTypesSummary(data) {
            if (!data || data.length === 0) {
                this.commissionTypesSummary =
                    "Nenhum tipo de comissão encontrado";
                this.commissionTypesPriority = "normal";
                this.commissionTypesBadge = null;
                return;
            }

            const highConcentration = data.filter(
                (commission) => commission.percentage > 70,
            ).length;
            const mediumConcentration = data.filter(
                (commission) => commission.percentage > 50,
            ).length;

            if (highConcentration > 0) {
                this.commissionTypesPriority = "warning";
                this.commissionTypesBadge = "Alta concentração";
                this.commissionTypesSummary = `${data.length} tipos de comissão, concentração alta`;
            } else if (mediumConcentration > 0) {
                this.commissionTypesPriority = "warning";
                this.commissionTypesBadge = "Concentração média";
                this.commissionTypesSummary = `${data.length} tipos de comissão, concentração média`;
            } else {
                this.commissionTypesPriority = "normal";
                this.commissionTypesBadge = `${data.length} tipos de comissão`;
                this.commissionTypesSummary = `${data.length} tipos de comissão distribuídos`;
            }
        },

        updateRunoffSummary(runoff) {
            // O card já emite o resumo pronto (ui_summary): a leitura de
            // status e a formatação vivem no próprio card, não aqui.
            if (!runoff?.ui_summary) {
                this.runoffSummary = "Runoff não disponível";
                this.runoffPriority = "normal";
                this.runoffBadge = null;
                return;
            }

            this.runoffPriority = runoff.ui_summary.priority;
            this.runoffBadge = runoff.ui_summary.badge;
            this.runoffSummary = runoff.ui_summary.summary;
            this.runoffCollapsed = runoff.ui_summary.priority === "normal";
        },

        generateDossier() {
            this.generatingDossier = true;

            const userId = this.$route.params.user_id ?? this.user.id;

            axios
                .post(`/api/user/${userId}/dossier/generate`)
                .then((response) => {
                    this.generatingDossier = false;

                    if (response.data.success && response.data.download_url) {
                        // Abrir URL em nova aba
                        window.open(response.data.download_url, "_blank");
                    } else {
                        throw new Error("Resposta inválida do servidor");
                    }
                })
                .catch(() => {
                    this.generatingDossier = false;
                });
        },
    },
};
</script>
